import React, { useState, useEffect } from 'react';
import * as api from '../../api/user-app-api';
import { Button } from '../common/Button';
import { Trash } from '../icons';

export const CartScreen = ({ cart, setCart, setView, params }) => {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    // Static route options (no existing API in repo) — replace or fetch from API if available
    const [routes, setRoutes] = useState(null);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ Fetch categories
    const fetchCategories = async () => {
        try {
            const res = await api.fetchCategories();
            if (Array.isArray(res.data)) setCategories(res.data);
            else if (Array.isArray(res.data?.data)) setCategories(res.data.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fetch products by category_id
    const fetchProducts = async (categoryId) => {
        try {
            const res = await api.fetchProductsByCategory({ category_id: categoryId });
            if (Array.isArray(res.data)) setProducts(res.data);
            else if (Array.isArray(res.data?.data)) setProducts(res.data.data);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to load products');
        }
    };

    // ✅ Fetch available dates
    const fetchAvailableDates = async () => {
        try {
            const res = await api.fetchAvailableDates();
            setAvailableDates(res.data.available_dates || []);
            setBlockedDates(res.data.blocked_dates || []);
        } catch (err) {
            console.error("Error fetching available dates:", err);
        }
    };

    // ✅ Fetch user addresses
    const fetchAddresses = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const res = await api.fetchUserAddresses({ user_id: userId });
            if (res.data.status === "success") {
                const list = res.data.data;
                setAddresses(list);

                if (list.length > 0 && !selectedAddress) {
                    setSelectedAddress(list[list.length - 1]);
                }

            }
        } catch (err) {
            console.error('Error loading addresses:', err);
        }
    };

    // ✅ Fetch user addresses
    // Fetch routes (API returns plain array)
    const fetchRoutes = async () => {
        try {
            const res = await api.fetchRoutes();

            if (Array.isArray(res.data)) {
                setRoutes(res.data);  // ← correct
            } else if (Array.isArray(res.data?.data)) {
                setRoutes(res.data.data);
            } else {
                setRoutes([]);
            }
        } catch (err) {
            console.error("Error loading routes:", err);
            setRoutes([]);
        }
    };


    useEffect(() => {
        fetchCategories();
        fetchAvailableDates();
        fetchAddresses();
        fetchRoutes();

        if (params?.newAddress) {
            setSelectedAddress(params.newAddress);
        }

        if (params?.goToStep) {
            setStep(params.goToStep);
        }

        if (params?.initialCategory?.id) {
            setSelectedCategory(params.initialCategory);
            fetchProducts(params.initialCategory.id);
        }
    }, []);


    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        fetchProducts(category.id);
    };

    // ✅ Product click adds directly to cart (no quantity required)
    const handleProductSelect = (product) => {
        const existingItem = cart.find(item => item.product_id === product.id);
        const price = parseFloat(product.price_per_unit || 0);

        if (existingItem) {
            alert(`${product.name} is already in the cart.`);
        } else {
            const newItem = {
                id: Date.now(),
                product_id: product.id,
                category_id: selectedCategory.id,
                category: selectedCategory.name,
                product: product.name,
                weight: 1,
                price: price
            };
            setCart([...cart, newItem]);
        }
    };

    const handleDeleteItem = (cart_id) => {
        setCart(cart.filter(item => item.id !== cart_id && item.cart_id !== cart_id));
    };

    const totalSellingPrice = cart.reduce(
        (sum, item) => sum + parseFloat(item.price || 0),
        0
    );

    const handleProceedToSummary = () => {
        if (cart.length > 0 && selectedRoute && selectedAddress) {
            setView('orderSummary', { cart, address: selectedAddress, route: selectedRoute });
        } else {
            alert("Please select an address and a route before continuing.");
        }
    };

    if (loading) return <div className="p-4 text-center">Loading Categories...</div>;
    if (error) return <div className="p-4 text-center text-red-600">{error}</div>;

    // === STEP 1: PRODUCT SELECTION ===
    if (step === 1) {
        return (
            <div className="relative h-[calc(100vh-80px)] overflow-y-auto pb-24">
                <div className="p-4 pt-0 mb-16">
                    {/* Summary */}
                    <div className="bg-white p-4 rounded-xl shadow-lg mb-6 border border-gray-100">
                        <p className="text-sm text-gray-600">
                            Total Selling Price: <span className="font-bold text-[#01a499]">₹{totalSellingPrice.toFixed(2)}</span>
                        </p>
                    </div>

                    {/* Cart items */}
                    <div className="space-y-4 mb-8">
                        {cart.map(item => (
                            <div key={item.id} className="flex justify-between items-center bg-[#01a499]/10 p-3 rounded-xl shadow-sm">
                                <div>
                                    <p className="font-semibold text-[#01a499]">{item.product} ({item.category})</p>
                                    <p className="text-xs text-gray-600">Price: ₹{item.price}</p>
                                </div>
                                <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700 p-2 rounded-full transition">
                                    <Trash className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                    </div>

                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-t pt-4">Select Products</h3>

                    {/* Categories */}
                    <div className="grid grid-cols-3 items-center gap-2 mb-4">
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => handleCategorySelect(cat)}
                                className={`flex flex-col p-2 rounded-xl items-center text-xs font-semibold border transition duration-150 ${selectedCategory?.id === cat.id
                                    ? 'bg-[#01a499] text-white border-[#01a499] shadow-md shadow-[#01a499]/25'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-[#01a499]/10 hover:border-[#01a499]/30'
                                    }`}
                            >
                                {cat.image_url ? (
                                    <img
                                        src={cat.image_url}
                                        alt={cat.name}
                                        className="w-12 h-12 object-cover mb-2"
                                    />
                                ) : (
                                    null
                                )}
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Products */}
                    {selectedCategory && (
                        <div className="bg-[#01a499]/10 p-4 rounded-xl space-y-3">
                            <h4 className="font-bold text-[#01a499]">Select a Product from {selectedCategory.name}</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {products.map(prod => (
                                    <button
                                        style={{ alignSelf: 'center' }}
                                        key={prod.id}
                                        onClick={() => handleProductSelect(prod)}
                                        className={`flex flex-col p-2 rounded-xl items-center duration-150 p-2 rounded-lg text-sm font-medium border transition ${cart.find(item => item.product_id === prod.id)
                                            ? 'bg-[#01a499] text-white border-[#01a499] shadow-sm shadow-[#01a499]/25'
                                            : 'bg-white text-gray-800 border-gray-300 hover:bg-[#01a499]/10 hover:border-[#01a499]/30'
                                            }`}
                                    >
                                        {prod.image_url ? (
                                            <img
                                                src={prod.image_url}
                                                alt={prod.name}
                                                className="w-12 h-12 object-cover mb-2"
                                            />
                                        ) : (
                                            null
                                        )}
                                        {prod.name} (₹{prod.price_per_unit}/{prod.unit})
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Fixed Bottom Button */}
                <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t pb-7">
                    <Button onClick={() => setStep(2)} disabled={cart.length === 0}>
                        Proceed to Pickup Details ({cart.length} Items)
                    </Button>
                </div>
            </div>
        );
    }

    // === STEP 2: ADDRESS SELECTION ===
    if (step === 2) {
        return (
            <div className="relative h-[calc(100vh-80px)] overflow-y-auto pb-24">
                <div className="p-4 pt-0 mb-16">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Select Pickup Address</h3>

                    <div className="space-y-3">
                        {addresses.length > 0 ? (
                            addresses.map((addr, i) => (
                                <div
                                    key={i}
                                    onClick={() => setSelectedAddress(addr)}
                                    className={`p-4 rounded-xl border cursor-pointer transition ${selectedAddress?.id === addr.id
                                        ? 'border-[#01a499] bg-[#01a499]/10'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                        }`}
                                >
                                    {/* Title Line */}
                                    <p className="font-medium text-gray-800">
                                        {addr.house_no}, {addr.building_name}
                                    </p>

                                    {/* Roads */}
                                    <p className="text-sm text-gray-700">
                                        Road 1: {addr.road_1}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Road 2: {addr.road_2}
                                    </p>

                                    {/* Landmark */}
                                    <p className="text-sm text-gray-700">
                                        Landmark: {addr.landmark}
                                    </p>

                                    {/* City + State + Pincode */}
                                    <p className="text-sm text-gray-600 mt-1">
                                        {addr.city}, {addr.state} - {addr.pincode}
                                    </p>
                                </div>

                            ))
                        ) : (
                            <div className="p-4 bg-gray-100 rounded-xl text-center">
                                <p className="text-gray-600 mb-3">No addresses found</p>
                                <Button onClick={() => setView('myAddress', { returnTo: 'cart', goToStep: 3 })}>
                                    Add Address
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Fixed Bottom Buttons */}
                <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t pb-7 flex space-x-2">
                    <Button onClick={() => setStep(1)} className="!bg-gray-500 w-1/3">Back</Button>
                    <Button onClick={() => setStep(3)} disabled={!selectedAddress} className="w-2/3">
                        Continue to Route
                    </Button>
                </div>
            </div>
        );
    }
    // === STEP 3: ROUTE SELECTION ===
    if (step === 3) {
        return (
            <div className="relative h-[calc(100vh-80px)] overflow-y-auto pb-24">
                <div className="p-4 pt-0 mb-16">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Select Pickup Route</h3>

                    <div className="space-y-3">
                        {routes && routes.length > 0 ? (
                            routes.map((r) => (
                                <div
                                    key={r.id}
                                    onClick={() => setSelectedRoute(r)}
                                    className={`p-4 rounded-xl border cursor-pointer transition ${selectedRoute?.id === r.id
                                        ? 'border-[#01a499] bg-[#01a499]/10'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                        }`}
                                >
                                    <p className="font-medium text-gray-800">{r.name}</p>
                                    <p className="font-semibold text-gray-800 mt-2 block">{r.route}</p>

                                    {/* Route link button */}
                                    {r.link && (
                                        <a
                                            href={r.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 text-sm underline mt-2 block"
                                        >
                                            View Route on Map
                                        </a>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">No routes found</p>
                        )}
                    </div>

                </div>

                {/* Fixed Bottom Buttons */}
                <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t pb-7 flex space-x-2">
                    <Button onClick={() => setStep(2)} className="!bg-gray-500 w-1/3">Back</Button>
                    <Button onClick={handleProceedToSummary} disabled={!selectedRoute} className="w-2/3">
                        Continue to Summary
                    </Button>
                </div>
            </div>
        );
    }
};
